import { type ComponentProps } from 'react'
import { ArrowRight } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { HighlightLogo } from '@highlight/ui'

interface NotchToastRowProps extends Omit<ComponentProps<'button'>, 'title'> {
  /** Names the click for the notch's toast analytics. */
  control: string
  title: string
  /** The quieter clause after the title, usually what the click does. */
  detail?: string
}

/**
 * The notch's one-line toast: Highlight's mark, a sentence, and an arrow. The whole row is the click.
 * 5 in and 5 between, so the 14 mark centred in its 20 lane lands 12 from the edge, as the design draws it.
 */
export function NotchToastRow({ control, title, detail, className, ...props }: NotchToastRowProps) {
  return (
    <button
      type="button"
      data-notch-control={control}
      className={twMerge(
        'type-ui-sm text-foreground hover:bg-element-hover flex h-7 w-full items-center gap-[5px] rounded-xl px-[5px] text-left transition-colors',
        className,
      )}
      {...props}
    >
      <span className="grid size-5 shrink-0 place-items-center">
        <HighlightLogo size={14} tone="brand">
          <HighlightLogo.Icon />
        </HighlightLogo>
      </span>
      <span className="min-w-0 flex-1 truncate">
        <span className="font-medium">{title}</span>
        {detail && <span className="text-foreground-secondary"> {detail}</span>}
      </span>
      <ArrowRight aria-hidden="true" className="text-foreground-secondary mx-0.5 size-3.5 shrink-0" />
    </button>
  )
}
